"use client";

/**
 * Bridges next-auth session state → PostHog identify / alias / reset.
 *
 * Meex mounts <SessionProvider> per-layout (auth/public/dashboard),
 * NOT at the root. So we CANNOT use `useSession()` from RootLayout —
 * it would throw "must be wrapped in <SessionProvider />".
 *
 * Instead we call `getSession()` (fetch-based, no provider needed) and
 * re-check on:
 *   - initial mount
 *   - tab focus / visibility change (catches login/logout)
 *   - the "storage" event with key "nextauth.message" that next-auth
 *     broadcasts cross-tab on sign-in / sign-out (instant sync)
 *
 * Behavior:
 *   - Unauthenticated: PostHog keeps its anonymous distinct_id.
 *   - First transition to authenticated: alias() to merge the anonymous
 *     journey into the user, then identify() with person props.
 *   - Subsequent authenticated checks: identify() only when userId
 *     changes (idempotent).
 *   - Transition to unauthenticated (logout): reset() so the next
 *     visitor is not attributed to the previous user.
 *
 * Never throws — all analytics calls are wrapped in `safe()`.
 */

import { useEffect, useRef } from "react";
import { getSession } from "next-auth/react";
import {
  alias,
  identify,
  reset,
  group,
  track,
  startSessionRecording,
  stopSessionRecording,
} from "./track";
import { EVENTS } from "./events";
import { canRecordPosthogSession, isPosthogEnabled } from "./posthog-client";

export default function IdentifyBridge() {
  const lastUserId = useRef(null);
  const aliased = useRef(false);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!isPosthogEnabled()) return;

    let cancelled = false;

    async function syncSession() {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const session = await getSession();
        if (cancelled) return;

        const userId = session?.user?.sub || session?.user?.id || null;
        const u = session?.user;
        const isAdmin = Boolean(u?.admin_tier || u?.tier?.admin_tier);

        if (!canRecordPosthogSession() || isAdmin) {
          stopSessionRecording();
        } else {
          startSessionRecording();
        }

        // Logout
        if (!userId && lastUserId.current) {
          track(EVENTS.LOGOUT);
          reset();
          lastUserId.current = null;
          aliased.current = false;
          return;
        }

        // Login (or first authenticated check)
        if (userId && userId !== lastUserId.current) {
          const isFirstIdentify = !aliased.current;
          if (isFirstIdentify) {
            alias(userId);
            aliased.current = true;
          }

          identify(userId, {
            email: u.email || undefined,
            name: u.name || undefined,
            given_name: u.given_name || undefined,
            family_name: u.family_name || undefined,
            admin_tier: u.admin_tier || u.tier?.admin_tier || undefined,
            is_admin: isAdmin,
            is_impersonating: Boolean(session.impersonating),
            tier: u.tier?.name || u.tier?.tier || undefined,
            provider_type: u.providerType || undefined,
            locale: u.locale || undefined,
          });

          if (u.providerId) {
            group("provider", u.providerId, {
              name: u.providerName || undefined,
            });
          }

          // Fire login/signup event. We can't reliably distinguish new
          // signups from returning logins here (next-auth's `isNewUser`
          // lives server-side in the signIn callback). This fires
          // LOGIN_SUCCESS on every fresh authentication in this tab;
          // SIGNUP_COMPLETED is fired server-side elsewhere when we
          // actually create a user profile row.
          track(EVENTS.LOGIN_SUCCESS, {
            user_sub: userId,
            is_admin: isAdmin,
            entry_url:
              typeof window !== "undefined" ? window.location.href : undefined,
            entry_pathname:
              typeof window !== "undefined"
                ? window.location.pathname
                : undefined,
            entry_referrer:
              typeof document !== "undefined" ? document.referrer : undefined,
          });

          lastUserId.current = userId;
        }
      } catch {
        // Never let analytics break the app
      } finally {
        inFlight.current = false;
      }
    }

    // Initial check
    syncSession();

    // Re-check on tab focus / visibility (catches login/logout)
    const onVisibility = () => {
      if (document.visibilityState === "visible") syncSession();
    };
    const onFocus = () => syncSession();

    // next-auth broadcasts sign-in/sign-out cross-tab via localStorage
    // key "nextauth.message"; picking this up is instant.
    const onStorage = (e) => {
      if (e.key === "nextauth.message") syncSession();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return null;
}
